import { TTicketDetails } from '@daymade/api-contracts';
import { GameType } from '@daymade/common-lib/dbEnums';
import { useUserStore } from 'hooks/stores/useUserStore';
import useCost from 'hooks/token-town/useCost';
import { useMemo, useRef, useState } from 'react';
import { INSUFFICIENT_TOKENS_MESSAGE } from 'utils/token-town/InsufficientTokenMessage';
import { getNumberOfTicketEntriesForDrawType, getNumberOfEntriesAvailableToPlayer } from 'utils/token-town/getRemainingEntries';

export function usePurchaseEntries(props: {
  tokenRedemptionCode: string;
  gameType: string;
  existingTickets: TTicketDetails[];
  entryCap: number | null;
  entriesSold: number;
  tokenCost: number;
  userTokenBalance: number;
  maxPlayerEntries: number | null;
}) {
  const userStore = useUserStore();
  const {
    tokenRedemptionCode,
    gameType,
    existingTickets,
    entryCap,
    entriesSold,
    tokenCost,
    userTokenBalance,
    maxPlayerEntries,
  } = props;

  const hasBeenCalled = useRef(false);
  const propsString = useRef('');
  const serialisedProps = JSON.stringify(props);
  if (!hasBeenCalled.current) {
    hasBeenCalled.current = true;
    console.log('usePurchaseEntries has been called for player ' + userStore.currentUser?.id + ' ' + gameType + ' for the first time ' + serialisedProps);
    propsString.current = serialisedProps;
  } else {
    if (propsString.current != serialisedProps) {
      console.log('usePurchaseEntries has been called for player ' + userStore.currentUser?.id + ' ' + gameType +  ' again, props changed ' + JSON.stringify(props));
      propsString.current = serialisedProps;
    } else {
      console.log('usePurchaseEntries has been called for player ' + userStore.currentUser?.id + ' ' + gameType +  ' again props same');
    }
  }
  const [numberOfEntries, setNumberOfEntries] = useState(1);
  const [selectedNumbers, setSelectedNumbers] = useState<(number | null)[]>([]);
  const [inputError, setInputError] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const totalTokenCost = useCost(numberOfEntries, tokenCost);
  const hasEnoughTokens = userTokenBalance >= totalTokenCost;

  const remainingAllowedEntries = useMemo(
    () =>
      getNumberOfEntriesAvailableToPlayer(
        existingTickets,
        tokenRedemptionCode,
        maxPlayerEntries
      ),
    [existingTickets, tokenRedemptionCode, maxPlayerEntries]
  );

  const entryCountForPlayerForDraw = useMemo(
    () =>
      getNumberOfTicketEntriesForDrawType(
        existingTickets,
        tokenRedemptionCode
      ),
    [existingTickets, tokenRedemptionCode]
  );

  const maxEntriesAvailableForPlayer = useMemo(() => {
    const maxEntriesAvailableForPlayer = Math.min(
      entryCap != null
        ? Math.max(0, entryCap - entriesSold)
        : Infinity,
      // Only let people buy 5 entries at a time
      5
    );

    if (
      remainingAllowedEntries &&
      maxEntriesAvailableForPlayer > remainingAllowedEntries
    ) {
      return remainingAllowedEntries;
    }

    return maxEntriesAvailableForPlayer;
  }, [entryCap, entriesSold, remainingAllowedEntries]);

  const buttonText = useMemo(() => {
    const logging = `Computing usePurchaseEntries.buttonText maxPlayerEntries=${maxPlayerEntries} remainingAllowedEntries=${remainingAllowedEntries} entryCap=${entryCap} entriesSold=${entriesSold}`;
    if (entryCap != null && entriesSold >= entryCap) {
        console.log(logging + ' returning NOT AVAILABLE FOR PURCHASE');
        return 'NOT AVAILABLE FOR PURCHASE';
    }
    if (maxPlayerEntries && remainingAllowedEntries === 0) {
      if (maxPlayerEntries == 1) {
        console.log(logging + ' returning YOU HAVE ALREADY ENTERED');
        return 'YOU HAVE ALREADY ENTERED';
      } else {
        console.log(logging + ' returning YOU ALREADY HAVE ' + entryCountForPlayerForDraw + ' ENTRIES');
        return 'YOU ALREADY HAVE ' + entryCountForPlayerForDraw + ' ENTRIES';
      }
    }

    if (!hasEnoughTokens) {
      console.log(`${logging} hasEnoughTokens=${hasEnoughTokens} returning INSUFFICIENT_TOKENS_MESSAGE`);
      return INSUFFICIENT_TOKENS_MESSAGE;
    }

    if (totalTokenCost > 0) {
      const result = `ENTER NOW FOR ${totalTokenCost} TOKEN${totalTokenCost > 1 ? 'S' : ''}`;
      console.log(`${logging} totalTokenCost=${totalTokenCost} returning ${result}`);
      return result;
    }

    console.log(`${logging} returning ENTER NOW`);
    return 'ENTER NOW';
  }, [
    hasEnoughTokens,
    maxPlayerEntries,
    totalTokenCost,
    remainingAllowedEntries,
    entryCountForPlayerForDraw,
    maxEntriesAvailableForPlayer
  ]);

  // Main CTA button disabled state
  const ctaButtonDisabled = useMemo(() => {
    return (
      confirmLoading ||
      maxEntriesAvailableForPlayer < 1 ||
      remainingAllowedEntries === 0 ||
      !hasEnoughTokens ||
      inputError ||
      (gameType !== GameType.RAFFLE && selectedNumbers.length === 0) ||
      selectedNumbers.some((e) => !e)
    );
  }, [confirmLoading, maxEntriesAvailableForPlayer, remainingAllowedEntries, hasEnoughTokens, inputError, gameType, selectedNumbers]);
  // + / - entry selector controls disabled state
  const entryControlDisabled = useMemo(() => {
    return confirmLoading || maxEntriesAvailableForPlayer < 1;
  }, [confirmLoading, maxEntriesAvailableForPlayer]);

  return {
    numberOfEntries,
    setNumberOfEntries,
    selectedNumbers,
    setSelectedNumbers,
    inputError,
    setInputError,
    confirmLoading,
    setConfirmLoading,
    totalTokenCost,
    hasEnoughTokens,
    maxEntriesAvailableForPlayer,
    buttonText,
    ctaButtonDisabled,
    entryControlDisabled,
  };
}
